import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from 'react';
import { createPortal } from 'react-dom';
import {
  planSelections,
  type GameState,
  type InstanceId,
  type PlannedCard,
  type TargetCandidate,
} from '@cards/engine';
import { getCardModeLabel } from '@cards/cards';
import { Badge } from './kit/index.js';
import { MOTION_DURATIONS } from './kit/tokens.js';
import { CardGlyph } from './CardGlyph.js';
import { cardInfoDataFromDefinition } from './CardInfoCard.js';
import { CardInfoPopover } from './CardInfoPopover.js';
import { CardBackGlyph } from './CardBackGlyph.js';
import { cardName } from './locale.js';
import type { PlannedCardKind } from './mechanics.js';
import type { OneShotLineupCard } from './TurnPlayback.js';
import { FULL_CARD_HOVER_OPEN_DELAY_MS, useHoverPopover } from './useHoverPopover.js';

export function planningModeLabel(definitionId: string, mode: string): string {
  return getCardModeLabel(definitionId, mode)?.label ?? mode;
}

const ONE_SHOT_KINDS = new Set<PlannedCardKind>(['SPELL', 'VIRUS', 'REACTION']);
/** An orb leaving the rail shrinks away in a quick beat (03 §3.3). */
const QUEUE_FADE_MS = MOTION_DURATIONS.quick;
type VisualPlanKind = PlannedCardKind | 'FIELD';

interface FiberTarget {
  key: string;
  candidate: Exclude<TargetCandidate, { kind: 'mode' }>;
}

/**
 * One animated fiber. Anchors are resolvers rather than measured rects: the
 * overlay re-reads them every frame, so a thread keeps its grip on a card that
 * is scrolling, sliding or mid-flight instead of drifting off it.
 */
export interface FiberOverlayLink {
  readonly key: string;
  readonly colorIndex: number;
  readonly source: () => Element | null;
  readonly target: () => Element | null;
  /** Bend direction of the initial sag; derived from the anchors when omitted. */
  readonly side?: 'viewer' | 'opponent';
  readonly active?: boolean;
  /** Devour clamps its vines onto the prey; cast rides a spark to the target. */
  readonly variant?: 'devour' | 'cast';
  /** Cast only: the spark bursts once the thread has landed. */
  readonly phase?: 'travel' | 'impact';
  /**
   * The fiber's own animation window: how long the thread takes to grow for a
   * cast, how long the whole snap-and-swallow lasts for a devour.
   */
  readonly revealMs?: number;
}

interface RenderedFiber {
  readonly link: FiberOverlayLink;
  readonly exiting: boolean;
}

/** Per-fiber animation state, kept out of React so painting never re-renders. */
interface FiberRuntime {
  seed: number;
  revealFrom: number;
  revealTo: number;
  revealAt: number | undefined;
  reveal: number;
  /** First painted frame; devour times its snap off this and never resets it. */
  bornAt: number | undefined;
  /** Last resolved anchors, reused by pointer hit-testing. */
  source: Element | null;
  target: Element | null;
  /** When a thread that grew in caught its target; drives the shiver and the knot. */
  latchedAt: number | undefined;
}

interface FiberNodes {
  root: SVGGElement | null;
  glow: SVGPathElement | null;
  threads: (SVGPathElement | null)[];
  spark: SVGCircleElement | null;
  /** Pixels riding the thread: motes flowing to the target, or a cast's trail. */
  motes: (SVGRectElement | null)[];
  /** The burst where the thread catches; positioned on the head every frame. */
  knot: SVGGElement | null;
}

const FIBER_SAMPLE_COUNT = 17;
const FIBER_REVEAL_MS = 220;
const FIBER_RETRACT_MS = 180;
/** One writhe cycle at rate 1; each fiber gets a seeded rate of its own. */
const FIBER_WRITHE_MS = 4200;
/** How long a thread shivers after it catches its target. */
const FIBER_LATCH_MS = MOTION_DURATIONS.latch;
/** One trip of a mote from the spell to its target at rate 1. */
const FIBER_MOTE_MS = 1600;
/** Behind a cast's spark: how far back each trailing pixel rides, in curve progress. */
const CAST_TRAIL_STEP = .045;
/** Fallback devour window when the caller does not name one. */
const DEVOUR_GRAB_MS = 760;
/** Fractions of the devour window: vines out, then the trap slams shut. */
const DEVOUR_SNAP_FROM = .28;
const DEVOUR_SNAP_TO = .42;

/**
 * How closed the trap is: 0 while the vines are still splayed open around the
 * prey, 1 once they have clamped. The shut itself is deliberately abrupt — a
 * flytrap is one of the fastest movements in the plant kingdom, and easing it
 * would turn the snap into a hug. Past the shut the grip keeps working at the
 * catch with a decaying cinch rather than freezing.
 */
export function devourGrip(progress: number): number {
  const snap = (progress - DEVOUR_SNAP_FROM) / (DEVOUR_SNAP_TO - DEVOUR_SNAP_FROM);
  if (snap <= 0) return 0;
  if (snap < 1) return 1 - (1 - snap) ** 4;
  const settle = Math.min(1, (progress - DEVOUR_SNAP_TO) / .34);
  return 1 + Math.sin(settle * Math.PI * 2) * .14 * (1 - settle);
}

function fiberSeed(key: string): number {
  let hash = 0;
  for (let index = 0; index < key.length; index += 1) hash = (hash * 31 + key.charCodeAt(index)) | 0;
  return Math.abs(hash);
}

function prefersReducedMotion(): boolean {
  return globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
}

/**
 * Viewport-level renderer for braided, living threads between two anchors.
 * The component owns measurement, motion and the enter/exit lifecycle; callers
 * only declare which anchors are connected.
 */
export function FiberOverlay({
  testId,
  links,
}: {
  testId: string;
  links: readonly FiberOverlayLink[];
}): ReactElement | null {
  const [rendered, setRendered] = useState<readonly RenderedFiber[]>(
    () => links.map((link) => ({ link, exiting: false })),
  );
  const [hovered, setHovered] = useState<readonly string[]>([]);
  const renderedRef = useRef(rendered);
  const runtimes = useRef(new Map<string, FiberRuntime>());
  const nodes = useRef(new Map<string, FiberNodes>());
  const exitTimers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  // Fibers already present on the very first render belong to a screen that is
  // only now appearing, so they start fully drawn: a draw-on storm on mount
  // would read as noise rather than as "this target was just picked".
  const mounted = useRef(false);

  // Sync the rendered set with the declared links, holding departed fibers
  // mounted long enough to retract into their source.
  useEffect(() => {
    const declared = new Map(links.map((link) => [link.key, link]));
    setRendered((current) => {
      const next: RenderedFiber[] = current.map((entry) => {
        const link = declared.get(entry.link.key);
        return link === undefined
          ? entry.exiting ? entry : { link: entry.link, exiting: true }
          : { link, exiting: false };
      });
      const known = new Set(current.map((entry) => entry.link.key));
      for (const link of links) if (!known.has(link.key)) next.push({ link, exiting: false });
      // Link identity matters as well as the key: a re-declared link is how a
      // cast advances to its impact phase and how an orb hover marks itself.
      const unchanged = next.length === current.length
        && next.every((entry, index) => entry.exiting === current[index]!.exiting
          && entry.link === current[index]!.link);
      return unchanged ? current : next;
    });
  }, [links]);

  // Layout-phase so the runtimes exist before the painting effect below runs.
  useLayoutEffect(() => {
    renderedRef.current = rendered;
    for (const entry of rendered) {
      const existing = runtimes.current.get(entry.link.key);
      const drawn = mounted.current ? 0 : 1;
      const runtime = existing ?? {
        seed: fiberSeed(entry.link.key),
        revealFrom: drawn,
        revealTo: 1,
        revealAt: undefined,
        reveal: drawn,
        bornAt: undefined,
        source: null,
        target: null,
        latchedAt: undefined,
      };
      if (entry.exiting && runtime.revealTo !== 0) {
        runtime.revealFrom = runtime.reveal;
        runtime.revealTo = 0;
        runtime.revealAt = undefined;
      }
      runtimes.current.set(entry.link.key, runtime);
    }
    // Retraction is time-based, so the unmount timer mirrors its duration.
    const live = new Set(rendered.map((entry) => entry.link.key));
    for (const [key, timer] of exitTimers.current) {
      if (rendered.some((entry) => entry.link.key === key && entry.exiting)) continue;
      clearTimeout(timer);
      exitTimers.current.delete(key);
    }
    for (const [key] of runtimes.current) {
      if (!live.has(key)) {
        runtimes.current.delete(key);
        nodes.current.delete(key);
      }
    }
    for (const entry of rendered) {
      if (!entry.exiting || exitTimers.current.has(entry.link.key)) continue;
      const { key } = entry.link;
      exitTimers.current.set(key, setTimeout(() => {
        exitTimers.current.delete(key);
        setRendered((current) => current.filter((item) => item.link.key !== key));
      }, FIBER_RETRACT_MS));
    }
  }, [rendered]);

  useEffect(() => () => {
    for (const timer of exitTimers.current.values()) clearTimeout(timer);
    exitTimers.current.clear();
  }, []);

  const paint = useCallback((now: number, animated: boolean): void => {
    const entries = renderedRef.current;
    if (entries.length === 0) return;
    // All anchors are read before anything is written: mixing the two would
    // force a layout flush per fiber instead of one per frame.
    const measured = entries.map((entry) => {
      const runtime = runtimes.current.get(entry.link.key);
      if (runtime === undefined) return undefined;
      runtime.source = entry.link.source();
      runtime.target = entry.link.target();
      return {
        entry,
        runtime,
        sourceRect: runtime.source?.getBoundingClientRect(),
        targetRect: runtime.target?.getBoundingClientRect(),
      };
    });
    for (const item of measured) {
      if (item === undefined) continue;
      const { entry, runtime, sourceRect, targetRect } = item;
      const group = nodes.current.get(entry.link.key);
      if (group === undefined || sourceRect === undefined || targetRect === undefined) continue;
      runtime.bornAt ??= now;
      const growing = runtime.revealTo > runtime.revealFrom;
      const duration = growing ? entry.link.revealMs ?? FIBER_REVEAL_MS : FIBER_RETRACT_MS;
      runtime.revealAt ??= now;
      const elapsed = animated ? Math.min(1, (now - runtime.revealAt) / Math.max(1, duration)) : 1;
      const eased = growing ? 1 - (1 - elapsed) ** 3 : elapsed ** 2;
      runtime.reveal = runtime.revealFrom + (runtime.revealTo - runtime.revealFrom) * eased;
      // Seeded rate and offset: without them every fiber on the board beats in
      // lockstep and the whole overlay reads as one progress indicator.
      const rate = .78 + (runtime.seed % 9) * .055;
      const writhe = animated
        ? (now / FIBER_WRITHE_MS) * Math.PI * 2 * rate + (runtime.seed % 360) * Math.PI / 180
        : 0;
      if (entry.link.variant === 'devour') {
        // The grab runs on the fiber's own clock rather than on `reveal`: the
        // vines are drawn out by CSS, and what the geometry animates is the
        // trap closing on the prey partway through that window.
        const window = Math.max(1, entry.link.revealMs ?? DEVOUR_GRAB_MS);
        const progress = animated ? Math.min(1, (now - runtime.bornAt) / window) : 1;
        const devour = devourFiberPaths(sourceRect, targetRect, {
          grip: devourGrip(progress),
          writhe,
        });
        group.glow?.setAttribute('d', devour.glowPath);
        devour.strandPaths.forEach((path, index) => group.threads[index]?.setAttribute('d', path));
        continue;
      }
      // A thread that grew in (never one drawn on mount, never one retracting)
      // catches its target the frame it arrives: the knot bursts there and the
      // braid thrashes, then calms back to its idle writhe.
      if (growing && runtime.revealFrom === 0 && runtime.reveal >= 1 && runtime.latchedAt === undefined) {
        runtime.latchedAt = now;
        group.root?.setAttribute('data-latched', 'true');
      }
      const latchAge = runtime.latchedAt === undefined ? Number.POSITIVE_INFINITY : now - runtime.latchedAt;
      const quiver = animated && latchAge < FIBER_LATCH_MS ? 1 + 1.8 * (1 - latchAge / FIBER_LATCH_MS) ** 2 : 1;
      const geometry = fiberPaths(sourceRect, targetRect, entry.link.side, {
        writhe,
        reveal: runtime.reveal,
        quiver,
      });
      group.glow?.setAttribute('d', geometry.glowPath);
      geometry.strandPaths.forEach((path, index) => group.threads[index]?.setAttribute('d', path));
      if (group.spark !== null) {
        group.spark.setAttribute('cx', fiberCoordinate(geometry.head.x));
        group.spark.setAttribute('cy', fiberCoordinate(geometry.head.y));
      }
      group.knot?.setAttribute('transform',
        `translate(${fiberCoordinate(geometry.head.x)} ${fiberCoordinate(geometry.head.y)})`);
      const cast = entry.link.variant === 'cast';
      group.motes.forEach((mote, index) => {
        if (mote === null) return;
        const size = Number(mote.getAttribute('width') ?? 3);
        // A cast drags a short trail of pixels behind its spark while it flies,
        // and drops it once it lands; a planned thread carries motes that flow
        // from the spell to its target, so the thread says which way it pulls.
        const travel = cast
          ? 1 - (index + 1) * CAST_TRAIL_STEP / Math.max(runtime.reveal, CAST_TRAIL_STEP)
          : ((now / FIBER_MOTE_MS) * rate + index / group.motes.length + (runtime.seed % 100) / 100) % 1;
        const point = geometry.pointAt(travel);
        mote.setAttribute('x', fiberCoordinate(point.x - size / 2));
        mote.setAttribute('y', fiberCoordinate(point.y - size / 2));
        const visible = cast
          ? (runtime.latchedAt === undefined && runtime.reveal > (index + 1) * CAST_TRAIL_STEP ? 1 - index * .25 : 0)
          : animated ? Math.sin(Math.PI * travel) * runtime.reveal : 0;
        mote.setAttribute('opacity', visible.toFixed(2));
      });
    }
  }, []);

  useLayoutEffect(() => {
    mounted.current = true;
    if (rendered.length === 0) return;
    const animated = !prefersReducedMotion();
    paint(performance.now(), animated);
    if (!animated) {
      // Nothing writhes, but anchors still move; repaint on the same events the
      // rest of the board uses rather than burning a frame loop.
      let frame: number | undefined;
      const schedule = (): void => {
        frame ??= requestAnimationFrame(() => {
          frame = undefined;
          paint(performance.now(), false);
        });
      };
      window.addEventListener('resize', schedule);
      window.addEventListener('scroll', schedule, true);
      return () => {
        if (frame !== undefined) cancelAnimationFrame(frame);
        window.removeEventListener('resize', schedule);
        window.removeEventListener('scroll', schedule, true);
      };
    }
    let frame = requestAnimationFrame(function step(now: number): void {
      if (!document.hidden) paint(now, true);
      frame = requestAnimationFrame(step);
    });
    return () => cancelAnimationFrame(frame);
  }, [paint, rendered]);

  // Hovering either end of a fiber isolates it. Hit-testing reuses the anchors
  // resolved by the last paint, so no extra DOM queries ride on pointer moves.
  useEffect(() => {
    if (rendered.length === 0) return;
    const update = (node: Node | null): void => {
      const next = node === null ? [] : renderedRef.current
        .filter((entry) => {
          const runtime = runtimes.current.get(entry.link.key);
          return runtime?.source?.contains(node) === true || runtime?.target?.contains(node) === true;
        })
        .map((entry) => entry.link.key);
      setHovered((current) => current.length === next.length
        && current.every((key, index) => key === next[index]) ? current : next);
    };
    const onPointerOver = (event: PointerEvent): void => {
      update(event.target instanceof Node ? event.target : null);
    };
    // pointerleave fires for every element the pointer steps off, so it cannot
    // be used to clear: a null relatedTarget is what "left the window" means.
    const onPointerOut = (event: PointerEvent): void => {
      if (event.relatedTarget == null) update(null);
    };
    document.addEventListener('pointerover', onPointerOver, true);
    document.addEventListener('pointerout', onPointerOut, true);
    return () => {
      document.removeEventListener('pointerover', onPointerOver, true);
      document.removeEventListener('pointerout', onPointerOut, true);
    };
  }, [rendered]);

  if (rendered.length === 0 || typeof document === 'undefined') return null;
  const isActive = (entry: RenderedFiber): boolean =>
    entry.link.active === true || hovered.includes(entry.link.key);
  return createPortal(
    <svg
      className="cards-spell-fibers"
      data-testid={testId}
      data-focused={rendered.some(isActive) ? 'true' : undefined}
      aria-hidden="true"
    >
      {rendered.map((entry) => {
        const { link } = entry;
        const strands = link.variant === 'devour' ? 7 : 3;
        const group = (): FiberNodes => {
          const existing = nodes.current.get(link.key);
          if (existing !== undefined) return existing;
          const created: FiberNodes = { root: null, glow: null, threads: [], spark: null, motes: [], knot: null };
          nodes.current.set(link.key, created);
          return created;
        };
        return (
          <g
            key={link.key}
            ref={(node) => { group().root = node; }}
            className="cards-spell-fiber"
            data-color-index={link.colorIndex % 5}
            data-active={isActive(entry) ? 'true' : undefined}
            data-variant={link.variant}
            data-phase={link.phase}
            data-exiting={entry.exiting ? 'true' : undefined}
          >
            <path
              d=""
              data-strand="glow"
              ref={(node) => { group().glow = node; }}
              pathLength={link.variant === 'devour' ? 1 : undefined}
            />
            {Array.from({ length: strands }, (_, strandIndex) => (
              <path
                key={strandIndex}
                d=""
                data-strand="thread"
                data-thread-index={strandIndex}
                ref={(node) => { group().threads[strandIndex] = node; }}
                pathLength={link.variant === 'devour' ? 1 : undefined}
              />
            ))}
            {link.variant === 'devour' ? null : Array.from(
              { length: link.variant === 'cast' ? 3 : 2 },
              (_, moteIndex) => {
                // Hard pixels, shrinking down a cast's trail.
                const size = link.variant === 'cast' ? 4 - moteIndex : 3;
                return (
                  <rect
                    key={`mote-${moteIndex}`}
                    data-strand="mote"
                    width={size}
                    height={size}
                    opacity="0"
                    ref={(node) => { group().motes[moteIndex] = node; }}
                  />
                );
              },
            )}
            {link.variant === 'cast'
              ? <circle r="3.5" data-strand="spark" ref={(node) => { group().spark = node; }} />
              : null}
            {link.variant === 'devour' ? null : (
              <g data-strand="knot" ref={(node) => { group().knot = node; }}>
                <rect data-knot="core" x="-4.5" y="-4.5" width="9" height="9" />
                <rect data-knot="chip" x="-1.5" y="-1.5" width="3" height="3" />
                <rect data-knot="chip" x="-1.5" y="-1.5" width="3" height="3" />
                <rect data-knot="chip" x="-1.5" y="-1.5" width="3" height="3" />
                <rect data-knot="chip" x="-1.5" y="-1.5" width="3" height="3" />
              </g>
            )}
          </g>
        );
      })}
    </svg>,
    document.body,
  );
}

interface RenderedQueueEntry extends OneShotLineupCard {
  exiting: boolean;
  playback: boolean;
}

function sameRenderedEntry(left: RenderedQueueEntry, right: RenderedQueueEntry): boolean {
  return left.instanceId === right.instanceId
    && left.playerId === right.playerId
    && left.defId === right.defId
    && left.kind === right.kind
    && left.faceDown === right.faceDown
    && left.revealing === right.revealing
    && left.exiting === right.exiting
    && left.playback === right.playback;
}

function planKind(plan: PlannedCard): VisualPlanKind {
  return plan.kind ?? 'FIELD';
}

function fiberTargets(plan: PlannedCard): FiberTarget[] {
  return planSelections(plan).flatMap((step, stepIndex) =>
    step.selection.flatMap((candidate, candidateIndex) => candidate.kind === 'mode' ? [] : [{
      key: `${step.stepId}:${stepIndex}:${candidateIndex}`,
      candidate,
    }]));
}

/** Attribute-value escape so a selector cannot be broken by an id's contents. */
function attributeValue(value: string): string {
  return value.replace(/["\\]/gu, '\\$&');
}

function elementWithTestId(testId: string): HTMLElement | null {
  return document.querySelector<HTMLElement>(`[data-testid="${attributeValue(testId)}"]`);
}

function targetElement(state: GameState, candidate: FiberTarget['candidate']): HTMLElement | null {
  if (candidate.kind === 'cell') return elementWithTestId(`cell-${candidate.cellId}`);
  if (candidate.kind === 'table') {
    const location = elementWithTestId(`board-location-${candidate.tableId}`);
    return location?.querySelector<HTMLElement>('.cards-board-front-line') ?? location;
  }
  if (candidate.kind === 'player') {
    return document.querySelector<HTMLElement>(`[data-player-id="${attributeValue(candidate.playerId)}"]`);
  }
  const instance = state.instances[candidate.instanceId];
  if (instance?.cellId !== undefined) {
    return elementWithTestId(`board-card-${candidate.instanceId}`)
      ?? elementWithTestId(`staged-card-${candidate.instanceId}`)
      ?? elementWithTestId(`cell-${instance.cellId}`);
  }
  return elementWithTestId(`hand-card-${candidate.instanceId}`);
}

interface FiberPoint {
  x: number;
  y: number;
}

function cubicPoint(
  start: FiberPoint,
  firstControl: FiberPoint,
  secondControl: FiberPoint,
  end: FiberPoint,
  progress: number,
): FiberPoint {
  const remaining = 1 - progress;
  return {
    x: remaining ** 3 * start.x
      + 3 * remaining ** 2 * progress * firstControl.x
      + 3 * remaining * progress ** 2 * secondControl.x
      + progress ** 3 * end.x,
    y: remaining ** 3 * start.y
      + 3 * remaining ** 2 * progress * firstControl.y
      + 3 * remaining * progress ** 2 * secondControl.y
      + progress ** 3 * end.y,
  };
}

function fiberCoordinate(value: number): string {
  return Number(value.toFixed(2)).toString();
}

function smoothFiberPath(points: readonly FiberPoint[]): string {
  const first = points[0];
  if (first === undefined) return '';
  let path = `M ${fiberCoordinate(first.x)} ${fiberCoordinate(first.y)}`;
  for (let index = 0; index < points.length - 1; index += 1) {
    const previous = points[Math.max(0, index - 1)]!;
    const start = points[index]!;
    const end = points[index + 1]!;
    const next = points[Math.min(points.length - 1, index + 2)]!;
    const firstControl = {
      x: start.x + (end.x - previous.x) / 6,
      y: start.y + (end.y - previous.y) / 6,
    };
    const secondControl = {
      x: end.x - (next.x - start.x) / 6,
      y: end.y - (next.y - start.y) / 6,
    };
    path += ` C ${fiberCoordinate(firstControl.x)} ${fiberCoordinate(firstControl.y)},`
      + ` ${fiberCoordinate(secondControl.x)} ${fiberCoordinate(secondControl.y)},`
      + ` ${fiberCoordinate(end.x)} ${fiberCoordinate(end.y)}`;
  }
  return path;
}

/**
 * Three braided strands along a sagging cubic between two anchors.
 *
 * `writhe` advances the braid in time — the strands actually crawl towards the
 * target instead of being a frozen zig-zag with a dash pattern sliding over it.
 * `reveal` truncates the curve so a fiber can grow out of its source and
 * retract back into it; `head` is the point the drawn part currently ends at.
 * `quiver` scales the braid's swing — above 1 the strands thrash, which is how
 * a thread that has just caught its target snaps taut and shivers.
 * `pointAt` walks the drawn part of the centre line (0 = source, 1 = head), for
 * whatever rides along the thread.
 */
export function fiberPaths(
  source: DOMRect,
  target: DOMRect,
  side?: 'viewer' | 'opponent',
  motion: { writhe?: number; reveal?: number; quiver?: number } = {},
): {
  glowPath: string;
  strandPaths: readonly string[];
  head: FiberPoint;
  pointAt: (progress: number) => FiberPoint;
} {
  const sourceX = source.left + source.width / 2;
  const sourceY = source.top + source.height / 2;
  const targetX = target.left + target.width / 2;
  const targetY = target.top + target.height / 2;
  // The sag always bows away from the target, which is what the explicit side
  // encoded; unspecified callers (playback cues) get it from the geometry.
  const resolvedSide = side ?? (targetY <= sourceY ? 'viewer' : 'opponent');
  const direction = resolvedSide === 'viewer' ? -1 : 1;
  const bend = Math.max(18, Math.min(72, Math.abs(targetY - sourceY) * 0.42));
  const sourceControlY = sourceY + direction * bend;
  const targetControlY = targetY - direction * Math.min(bend, Math.abs(targetY - sourceY) * 0.3);
  const start = { x: sourceX, y: sourceY };
  const firstControl = { x: sourceX, y: sourceControlY };
  const secondControl = { x: targetX, y: targetControlY };
  const end = { x: targetX, y: targetY };
  const distance = Math.hypot(targetX - sourceX, targetY - sourceY);
  const normal = distance === 0
    ? { x: 1, y: 0 }
    : { x: -(targetY - sourceY) / distance, y: (targetX - sourceX) / distance };
  const amplitude = Math.max(1.8, Math.min(4.6, distance * 0.019)) * Math.max(0, motion.quiver ?? 1);
  const writhe = motion.writhe ?? 0;
  const span = Math.max(0, Math.min(1, motion.reveal ?? 1));
  const basePoints = Array.from({ length: FIBER_SAMPLE_COUNT }, (_, index) => {
    const progress = (index / (FIBER_SAMPLE_COUNT - 1)) * span;
    return cubicPoint(start, firstControl, secondControl, end, progress);
  });
  const strandPaths = [0, (Math.PI * 2) / 3, (Math.PI * 4) / 3].map((phase) => {
    const points = basePoints.map((point, index) => {
      const progress = (index / (FIBER_SAMPLE_COUNT - 1)) * span;
      const envelope = Math.sin(Math.PI * progress);
      // A second, longer wave keeps the braid from reading as a clean sine.
      const braid = Math.sin(Math.PI * 4 * progress - writhe + phase)
        + Math.sin(Math.PI * 2.3 * progress - writhe * 0.62 + phase * 1.7) * 0.52;
      const offset = braid * 0.68 * amplitude * envelope;
      return {
        x: point.x + normal.x * offset,
        y: point.y + normal.y * offset,
      };
    });
    return smoothFiberPath(points);
  });
  return {
    glowPath: smoothFiberPath(basePoints),
    strandPaths,
    head: basePoints[basePoints.length - 1] ?? start,
    pointAt: (progress) => cubicPoint(start, firstControl, secondControl, end,
      Math.max(0, Math.min(1, progress)) * span),
  };
}

/** Sideways lean of each vine, so the six read as a cage and not as a flat X. */
const DEVOUR_VINE_SPREAD = [1, -1, .58, -.58, .86, -.86];

/**
 * A flytrap rather than a rope: a taut centre tendon plus six vines whose
 * widest point sits AT the prey, not midway, so they read as jaws thrown open
 * around it. `grip` closes them — at 0 the cage is splayed, at 1 the vines have
 * cinched onto the catch — and `writhe` keeps the lobes trembling while they
 * hold, harder once they are clamped than while they are still reaching.
 */
export function devourFiberPaths(
  source: DOMRect,
  target: DOMRect,
  motion: { grip?: number; writhe?: number } = {},
): { glowPath: string; strandPaths: readonly string[] } {
  const start = {
    x: source.left + source.width / 2,
    y: source.top + source.height / 2,
  };
  const end = {
    x: target.left + target.width / 2,
    y: target.top + target.height / 2,
  };
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const distance = Math.hypot(dx, dy);
  const normal = distance === 0 ? { x: 1, y: 0 } : { x: -dy / distance, y: dx / distance };
  const arc = Math.max(16, Math.min(54, distance * .17));
  const grip = Math.max(0, motion.grip ?? 0);
  const writhe = motion.writhe ?? 0;
  // Shutting collapses the flare almost completely; the remainder is what
  // still reads as vines wrapped around the prey rather than a bare line.
  const flare = arc * Math.max(.12, 1 - grip * .84);
  const tremor = arc * (.14 + Math.min(1, grip) * .1);
  const wovenPath = (threadIndex: number): string => {
    const lean = DEVOUR_VINE_SPREAD[threadIndex] ?? 1;
    const phase = threadIndex * Math.PI / 3;
    const points = Array.from({ length: 11 }, (_, index) => {
      const progress = index / 10;
      // Exponent > 1 pushes the widest point of the cage past the midpoint,
      // out where the prey is: the vines leave the trap nearly straight and
      // only bloom open once they are over their catch.
      const envelope = Math.sin(Math.PI * progress ** 1.55);
      const offset = lean * flare * envelope
        + Math.sin(Math.PI * 3.4 * progress + phase - writhe) * tremor * envelope;
      return {
        x: start.x + dx * progress + normal.x * offset,
        y: start.y + dy * progress + normal.y * offset,
      };
    });
    return smoothFiberPath(points);
  };
  const centrePath = `M ${fiberCoordinate(start.x)} ${fiberCoordinate(start.y)}`
    + ` L ${fiberCoordinate(end.x)} ${fiberCoordinate(end.y)}`;
  return {
    glowPath: centrePath,
    strandPaths: [centrePath, ...Array.from(
      { length: DEVOUR_VINE_SPREAD.length },
      (_, index) => wovenPath(index),
    )],
  };
}

function SpellFibers({
  state,
  plans,
  side,
  activeInstanceId,
  root,
}: {
  state: GameState;
  plans: readonly PlannedCard[];
  side: 'viewer' | 'opponent';
  activeInstanceId: InstanceId | undefined;
  root: HTMLElement | null;
}): ReactElement | null {
  // Anchors are resolved lazily by the overlay, so the closures below must read
  // the live state rather than the state of the render that created them.
  const stateRef = useRef(state);
  stateRef.current = state;
  const rootRef = useRef(root);
  rootRef.current = root;
  // Colour groups by SPELL, not by target ordinal: "which card is pulling this
  // thread" is the question the palette has to answer.
  const signature = plans
    .map((plan) => `${plan.instanceId}#${fiberTargets(plan).map((target) => target.key).join(',')}`)
    .join('|');
  const links = useMemo<readonly FiberOverlayLink[]>(() => plans.flatMap((plan, planIndex) =>
    fiberTargets(plan).map((target) => ({
      key: `${plan.instanceId}:${target.key}`,
      colorIndex: planIndex,
      side,
      active: activeInstanceId === plan.instanceId,
      source: () => rootRef.current?.querySelector(
        `[data-spell-queue-marker="${attributeValue(plan.instanceId)}"]`) ?? null,
      target: () => targetElement(stateRef.current, target.candidate),
    // Keyed on the signature rather than on `plans`: the array is rebuilt every
    // engine tick, but the closures only go stale when a target actually moves.
    }))), [activeInstanceId, signature, side]);

  // The queue also renders outside a battle board (deck lab previews); fibers
  // only make sense once there is a field for them to cross.
  const [inField, setInField] = useState(false);
  useLayoutEffect(() => {
    setInField(root?.closest('.cards-board-field') != null);
  }, [root]);

  // Mounted even while empty, so the first fiber a player draws animates in
  // like every later one; the overlay renders nothing until it has a link.
  if (!inField) return null;
  return <FiberOverlay testId={`spell-fibers-${side}`} links={links} />;
}

export interface SpellPlanProps {
  plans: readonly PlannedCard[];
  state: GameState;
  /** Only mechanics actually present in the deck; retained for the empty rail. */
  visibleKinds?: readonly PlannedCardKind[];
  disabled: boolean;
  onCancel: (instanceId: InstanceId) => void;
  side?: 'viewer' | 'opponent';
  /** Open-information surfaces such as the sandbox may edit either queue. */
  allowCancel?: boolean;
  playbackCards?: readonly OneShotLineupCard[];
}

/**
 * Visual one-shot queue embedded directly above/below the locations.
 * The circular card trigger is intentionally a native button: it combines
 * card inspection with the game-specific "remove from preparation" action.
 */
export function SpellPlan({
  plans,
  state,
  visibleKinds,
  disabled,
  onCancel,
  side = 'viewer',
  allowCancel = side === 'viewer',
  playbackCards = [],
}: SpellPlanProps): ReactElement {
  const [root, setRoot] = useState<HTMLElement | null>(null);
  const [activeInstanceId, setActiveInstanceId] = useState<InstanceId | undefined>(undefined);
  const exitTimers = useRef(new Map<InstanceId, ReturnType<typeof setTimeout>>());
  const hover = useHoverPopover<{ instanceId: InstanceId; defId: string; anchor: HTMLElement }>({
    openDelayMs: FULL_CARD_HOVER_OPEN_DELAY_MS,
  });
  const visiblePlans = useMemo(() => plans
    .filter((plan) => {
      const kind = planKind(plan);
      return kind !== 'FIELD' && ONE_SHOT_KINDS.has(kind);
    })
    .sort((left, right) => ('planSeq' in left ? left.planSeq : 0) - ('planSeq' in right ? right.planSeq : 0)), [plans]);
  const showsPlayback = playbackCards.length > 0;
  const entries = useMemo<readonly OneShotLineupCard[]>(() => {
    if (showsPlayback) return playbackCards;
    if (state.phase !== 'planning') return [];
    return visiblePlans.map((plan) => {
      const defId = state.instances[plan.instanceId]?.defId;
      return {
        instanceId: plan.instanceId,
        playerId: state.instances[plan.instanceId]?.ownerId ?? '',
        kind: planKind(plan) as OneShotLineupCard['kind'],
        ...(defId === undefined ? {} : { defId }),
        faceDown: false,
        revealing: false,
      };
    });
  }, [playbackCards, showsPlayback, state.instances, state.phase, visiblePlans]);
  const [renderedEntries, setRenderedEntries] = useState<readonly RenderedQueueEntry[]>(
    () => entries.map((entry) => ({ ...entry, exiting: false, playback: showsPlayback })),
  );

  useEffect(() => {
    setRenderedEntries((current) => {
      const incoming = new Map(entries.map((entry) => [entry.instanceId, entry]));
      const currentIds = new Set(current.map((entry) => entry.instanceId));
      const merged: RenderedQueueEntry[] = current.map((entry) => {
        const next = incoming.get(entry.instanceId);
        return next === undefined
          ? { ...entry, exiting: true }
          : { ...next, exiting: false, playback: showsPlayback };
      });
      for (const entry of entries) {
        if (!currentIds.has(entry.instanceId)) {
          merged.push({ ...entry, exiting: false, playback: showsPlayback });
        }
      }
      return merged.length === current.length
        && merged.every((entry, index) => sameRenderedEntry(entry, current[index]!))
        ? current
        : merged;
    });
  }, [entries, showsPlayback]);

  useEffect(() => {
    const exitingIds = new Set(renderedEntries
      .filter((entry) => entry.exiting)
      .map((entry) => entry.instanceId));
    for (const [instanceId, timer] of exitTimers.current) {
      if (exitingIds.has(instanceId)) continue;
      clearTimeout(timer);
      exitTimers.current.delete(instanceId);
    }
    for (const instanceId of exitingIds) {
      if (exitTimers.current.has(instanceId)) continue;
      exitTimers.current.set(instanceId, setTimeout(() => {
        exitTimers.current.delete(instanceId);
        setRenderedEntries((current) => current.filter((entry) => entry.instanceId !== instanceId));
      }, QUEUE_FADE_MS));
    }
  }, [renderedEntries]);

  useEffect(() => () => {
    for (const timer of exitTimers.current.values()) clearTimeout(timer);
    exitTimers.current.clear();
  }, []);

  const hasRelevantMechanic = visibleKinds === undefined
    || visibleKinds.some((kind) => ONE_SHOT_KINDS.has(kind));
  const popover = hover.value;
  const popoverData = popover === undefined ? undefined : cardInfoDataFromDefinition(popover.defId);
  // A removed queue card stays mounted briefly for its fade-out, but its
  // inspection preview belongs to the live plan rather than that exit-orb.
  const popoverPlanStillQueued = popover !== undefined
    && visiblePlans.some((plan) => plan.instanceId === popover.instanceId);

  const captureRoot = useCallback((element: HTMLElement | null): void => {
    setRoot(element);
  }, []);

  return (
    <section
      ref={captureRoot}
      className="cards-spell-queue"
      data-testid={`spell-queue-${side}`}
      data-onboarding={side === 'viewer' ? 'plan' : undefined}
      data-side={side}
      data-empty={entries.length === 0 ? 'true' : undefined}
      data-relevant={hasRelevantMechanic ? 'true' : undefined}
      aria-label={side === 'viewer' ? 'Моя очередь заклинаний' : 'Очередь заклинаний соперника'}
    >
      <div className="cards-spell-queue-list">
        {renderedEntries.map((entry) => {
          const plan = visiblePlans.find((candidate) => candidate.instanceId === entry.instanceId);
          const defId = entry.faceDown
            ? undefined
            : entry.defId ?? state.instances[entry.instanceId]?.defId;
          const mode = plan === undefined
            ? undefined
            : planSelections(plan)
              .flatMap((step) => step.selection)
              .find((candidate) => candidate.kind === 'mode');
          const modeLabel = mode?.kind === 'mode' && defId !== undefined
            ? planningModeLabel(defId, mode.id)
            : undefined;
          const canInspect = !entry.faceDown && defId !== undefined;
          const canCancel = !entry.playback && !entry.exiting && allowCancel && !disabled;
          const label = entry.faceDown || defId === undefined
            ? 'Подготовленная скрытая карта'
            : `${cardName(defId)}${modeLabel === undefined ? '' : `, режим: ${modeLabel}`}`;
          return (
            <button
              key={entry.instanceId}
              type="button"
              className="cards-spell-orb"
              data-testid={`planned-${entry.kind === 'REACTION' ? 'reaction' : entry.kind === 'VIRUS' ? 'virus' : 'spell'}-${entry.instanceId}`}
              data-spell-queue-marker={entry.instanceId}
              data-card-type={entry.kind}
              data-face-down={entry.faceDown ? 'true' : undefined}
              data-revealing={entry.revealing ? 'true' : undefined}
              data-exiting={entry.exiting ? 'true' : undefined}
              disabled={entry.exiting
                || (!entry.playback && allowCancel && disabled)
                || (!canCancel && !canInspect)}
              aria-label={`${label}${canCancel ? '. Нажмите, чтобы отменить подготовку.' : ''}`}
              title={label}
              onMouseEnter={(event) => {
                setActiveInstanceId(entry.instanceId);
                if (canInspect && defId !== undefined) {
                  hover.open({ instanceId: entry.instanceId, defId, anchor: event.currentTarget });
                }
              }}
              onMouseLeave={() => {
                setActiveInstanceId(undefined);
                hover.scheduleClose();
              }}
              onFocus={(event) => {
                setActiveInstanceId(entry.instanceId);
                if (canInspect && defId !== undefined) {
                  hover.open({ instanceId: entry.instanceId, defId, anchor: event.currentTarget });
                }
              }}
              onBlur={() => {
                setActiveInstanceId(undefined);
                hover.scheduleClose();
              }}
              onClick={() => {
                if (canCancel) {
                  hover.closeNow();
                  onCancel(entry.instanceId);
                }
              }}
            >
              <span className="cards-spell-orb-back" aria-hidden="true">
                <CardBackGlyph />
              </span>
              <span className="cards-spell-orb-glyph" aria-hidden="true">
                {defId === undefined ? <CardBackGlyph /> : <CardGlyph defId={defId} size={34} />}
              </span>
              {modeLabel === undefined ? null : (
                <Badge tone="neutral" className="cards-spell-orb-mode" aria-hidden="true">{modeLabel}</Badge>
              )}
            </button>
          );
        })}
      </div>
      {!showsPlayback && state.phase === 'planning' ? (
        <SpellFibers
          state={state}
          plans={visiblePlans}
          side={side}
          activeInstanceId={activeInstanceId}
          root={root}
        />
      ) : null}
      {popover === undefined || popoverData === undefined || !popoverPlanStillQueued ? null : (
        <CardInfoPopover
          anchor={popover.anchor}
          data={popoverData}
          testId={`planned-card-${popover.instanceId}-preview`}
          closing={hover.closing}
          onMouseEnter={hover.cancelClose}
          onMouseLeave={hover.scheduleClose}
        />
      )}
    </section>
  );
}
